<template>
    <div>
        <v-container v-if="isLoading">
            <LoadingComponent :isLoading="isLoading" />
        </v-container>
        <v-container v-else>
            <v-row class="d-flex justify-center ga-10">
                <v-col class="d-flex justify-center pt-12" cols="12">
                    <h1>SIGN UP</h1>
                </v-col>
                <v-col cols="12">
                    <p class="pb-4">Set your credentials!</p>
                    <v-form v-model="valid">
                        <v-text-field
                            variant="outlined"
                            v-model="name"
                            label="First Name"
                            :rules="[rules.required]"
                            required
                        ></v-text-field>

                        <v-text-field
                            variant="outlined"
                            v-model="surname"
                            label="Last Name"
                            :rules="[rules.required]"
                            required
                        ></v-text-field>
                        <v-text-field
                            variant="outlined"
                            v-model="password"
                            :append-inner-icon="
                                showPassword ? 'mdi-eye' : 'mdi-eye-off'
                            "
                            label="Password"
                            :rules="[rules.required]"
                            :type="showPassword ? 'text' : 'password'"
                            @click:append-inner="showPassword = !showPassword"
                            required
                        ></v-text-field>
                        <v-text-field
                            variant="outlined"
                            v-model="confirmPassword"
                            :append-inner-icon="
                                showConfirmationPassword
                                    ? 'mdi-eye'
                                    : 'mdi-eye-off'
                            "
                            label="Confirm Password"
                            :rules="[rules.required, passwordMatch]"
                            :type="
                                showConfirmationPassword ? 'text' : 'password'
                            "
                            @click:append-inner="
                                showConfirmationPassword =
                                    !showConfirmationPassword
                            "
                            required
                        ></v-text-field>
                    </v-form>
                </v-col>
                <v-col>
                    <v-btn
                        size="x-large"
                        block
                        color="primary"
                        @click="completeSignUp"
                        :disabled="!valid"
                        >SIGN UP</v-btn
                    >
                </v-col>
            </v-row>
        </v-container>
    </div>
</template>

<script>
import { ref, get, set, remove } from "firebase/database";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { db, auth } from "@/firebaseConfig";
import { mapActions } from "vuex"; // Import Vuex actions
import LoadingComponent from "@/components/LoadingComponent.vue";

export default {
    components: {
        LoadingComponent,
    },
    data() {
        return {
            name: "",
            surname: "",
            isLoading: false,
            password: "",
            confirmPassword: "",
            showPassword: false,
            showConfirmationPassword: false,
            valid: false,
            inviteData: null, // Holds the user invite data fetched from Firebase
            rules: {
                required: (value) => !!value || "Required.",
                passwordMatch: () => this.passwordMatch(),
            },
        };
    },
    methods: {
        ...mapActions(["setUser"]), // Map Vuex action to set user in the store
        passwordMatch() {
            return (
                this.password === this.confirmPassword ||
                "Passwords must match."
            );
        },
        /**
         * Fetches invitation data from Firebase using the email from the query parameters.
         * If the invite is invalid or expired, redirects the user to the homepage.
         *
         * @async
         * @returns {Promise<void>}
         */

        async fetchInviteData() {
            const inviteEmail = this.$route.query.email;
            if (!inviteEmail) {
                alert("Invalid or expired invite link.");
                this.$router.push("/");
                return;
            }

            //  Ensure email key matches how it was saved in Firebase
            const emailKey = inviteEmail.replace(/\./g, "_");

            const userRef = ref(db, `pendingInvites/${emailKey}`); // ✅ Fetch with formatted key
            try {
                const snapshot = await get(userRef);
                if (snapshot.exists()) {
                    this.inviteData = snapshot.val();
                } else {
                    alert("Invalid or expired invite link.");
                    this.$router.push("/");
                }
            } catch (error) {
                console.error(" Error fetching invite data:", error);
                alert("Error fetching invite data.");
                this.$router.push("/");
            }
        },

        /**
         * Completes the sign-up process for a new user.
         * - Creates the user in Firebase Authentication.
         * - Assigns system projects from Firebase.
         * - Saves the user data in Firebase Realtime Database.
         * - Removes the pending invite after sign-up.
         * - Updates Vuex store and starts listeners.
         * - Redirects the user to the dashboard.
         *
         * @async
         * @returns {Promise<void>}
         */
        async completeSignUp() {
            if (!this.inviteData) {
                alert("No invite data found.");
                return;
            }
            this.isLoading = true;

            try {
                //  Step 1: Create user in Firebase Auth with their new password
                const userCredential = await createUserWithEmailAndPassword(
                    auth,
                    this.inviteData.email,
                    this.password // User-defined password
                );
                const user = userCredential.user;

                //  Step 1: Define System Projects
                const systemProjectNames = [
                    "Lunch Break",
                    "Probleme tehnice",
                    "Timp fara documente",
                ];
                let userProjects = {}; // Object to store assigned projects

                for (const projectName of systemProjectNames) {
                    const projectRef = ref(db, "projects");
                    const projectSnapshot = await get(projectRef);

                    if (projectSnapshot.exists()) {
                        const projectsData = projectSnapshot.val();

                        // Find the project with the matching name
                        const projectEntry = Object.entries(projectsData).find(
                            ([id, data]) => data.name === projectName
                        );

                        if (projectEntry) {
                            const [projectId, projectData] = projectEntry;

                            // Assign project to user in the same format as "Lunch Break"
                            userProjects[projectId] = {
                                projectId: projectId,
                                name: projectData.name,
                                status: projectData.status || "active",
                                order:
                                    this.user?.projects?.[projectId]?.order ||
                                    99, // Default order
                                priority:
                                    this.user?.projects?.[projectId]
                                        ?.priority || "Normal", // Default priority
                            };
                        } else {
                            console.warn(
                                `⚠ Project '${projectName}' not found in Firebase.`
                            );
                        }
                    }
                }

                //  Step 2: Save user data in Firebase Database under `users/${user.uid}`
                const userRef = ref(db, `users/${user.uid}`); // Use Firebase Auth UID as key
                await set(userRef, {
                    email: this.inviteData.email,
                    role: this.inviteData.role,
                    name: this.name,
                    surname: this.surname,
                    accountStatus: "active",
                    userStatus: "loggedIn",
                    projects: userProjects,
                });

                //  Step 3: Remove the invite from `pendingInvites`
                await remove(
                    ref(
                        db,
                        `pendingInvites/${this.inviteData.email.replace(
                            /\./g,
                            "_"
                        )}`
                    )
                );

                //  Step 4: Update Vuex Store
                const userData = {
                    email: this.inviteData.email,
                    role: this.inviteData.role,
                    name: this.name,
                    surname: this.surname,
                    accountStatus: "active",
                    userStatus: "loggedIn",
                    projects: userProjects,
                };
                await this.setUser(userData);

                //  Step 5: Start Firebase Listeners (Wait for User Listener First)
                this.$store.dispatch("startUserListener", user.uid);

                //  Wait until the user data is fully loaded in Vuex
                await new Promise((resolve) => setTimeout(resolve, 1000)); // Small delay to ensure Vuex updates

                //  Delay Users Listener slightly to ensure stable loading
                setTimeout(() => {
                    this.$store.dispatch("startUsersListener");
                }, 500);

                alert(
                    "Your account has been activated, and you are now logged in."
                );
                this.$router.push("/userdashboard");
            } catch (error) {
                console.error(" Error completing sign-up:", error);
                alert(error.message);
            } finally {
                this.isLoading = false;
            }
        },
    },
    mounted() {
        this.fetchInviteData();
    },
};
</script>
